import clsx from 'clsx';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { RootState } from '@/src/reducers/types';
import { cn } from '@/src/util/ui';

import Folders from '../Folders';

const Folder = ({
  folder,
  projectId,
  selected,
  parentFolderId,
  parentFolders,
  onSelect,
  breadcrumbsMap,
  savedParentFolderId = null,
}) => {
  const [isToggled, setIsToggled] = useState(breadcrumbsMap.get(folder.id));
  const folderRef = useRef(null);

  const loggedUser = useSelector((state: RootState) => state.loggedUser);

  // Executes on component mount only
  useEffect(() => {
    if (parentFolderId === folder.id && folderRef.current) {
      folderRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickSubFolder = useCallback(
    (action) => {
      if (action !== 'toggle') {
        onSelect(folder, [...parentFolders, folder.name]);
      }

      setIsToggled((prev) => !prev);
    },
    [isToggled]
  );

  const isExpanded = !!folder.isExpanded;
  const isDisabled =
    (folder.accessList?.map((a) => a.id).indexOf(loggedUser.data.id) > -1 &&
      folder.accessList[folder.accessList?.map((a) => a.id).indexOf(loggedUser.data.id)].access ===
        'viewer') ||
    projectId;
  const showSubFolders = isToggled || isExpanded;

  const anchorActiveClasses =
    selected && selected.id === folder.id ? 'tw-text-cfo-blue-700' : 'tw-text-cfo-black';

  return (
    <li
      className={clsx(
        'folderRow',
        '[counter-reset:section] [counter-increment:section_3]',
        'tw-block',
        savedParentFolderId === folder.id && 'disabled'
      )}
    >
      <div
        className={clsx(
          'wrapper-folderRow',
          'tw-group',
          'tw-flex tw-items-center',
          'tw-py-[7px] tw-px-0',
          'hover:tw-bg-cfo-blue-200',
          selected && selected.id === folder.id && 'tw-bg-cfo-blue-200'
        )}
      >
        <div
          className={clsx(
            'container--folderSpacer',
            'before:tw-content-[counters(section,"")] before:tw-opacity-0 before:tw-py-[10px] before:tw-px-0 before:tw-tracking-[5px]'
            // hasSubFolders ? 'before:tw-px-[8px]' : 'before:tw-px-0'
          )}
        ></div>

        <AnchorLink
          className={cn(
            'accordion-toggle arrowContainer',
            'tw-block',
            'tw-text-cfo-black',
            'group-hover:tw-text-cfo-blue-700',
            anchorActiveClasses,
            showSubFolders && 'toggled'
          )}
          // data-toggle="collapse"
          // href={`#move-collapse-${folder.id}`}
          role="button"
          aria-expanded={showSubFolders}
          aria-controls={`move-collapse-${folder.id}`}
          onClick={() => handleClickSubFolder('toggle')}
        >
          <Icon
            className={clsx('tw-mr-[10px]')}
            icon={showSubFolders ? ICONS.dropdown : ICONS.dropdown_right}
          />
        </AnchorLink>

        <AnchorLink
          anchorRef={folderRef}
          className={cn(
            'container--folderName',
            'tw-flex tw-items-center',
            'tw-block',
            'tw-w-full',
            ' tw-font-brandonTextMedium',
            'group-hover:tw-text-cfo-blue-700',
            anchorActiveClasses,
            savedParentFolderId === folder.id &&
              'disabled tw-text-cfo-grey-600 tw-pointer-events-none group-hover:!tw-text-cfo-grey-600',
            isDisabled && 'disabled'
          )}
          // data-toggle="collapse"
          // href={`#move-collapse-${folder.id}`}
          role="button"
          aria-expanded={showSubFolders}
          aria-controls={`move-collapse-${folder.id}`}
          onClick={handleClickSubFolder}
        >
          <Icon className={clsx('tw-mr-[10px]')} icon={'folder'} />
          {folder.name}
        </AnchorLink>
      </div>
      <div
        className={clsx(
          'container--subFolder',
          folder.children.length === 0 && 'displayNone',
          !showSubFolders && 'collapse',
          showSubFolders && 'collapse in'
        )}
        style={{ height: 'auto' }}
        id={`move-collapse-${folder.id}`}
        aria-expanded={showSubFolders}
      >
        {folder.children.length > 0 && (
          <Folders
            projectId={projectId}
            folders={folder.children}
            onSelect={onSelect}
            selected={selected}
            document={document}
            parentFolderId={folder.id}
            savedParentFolderId={savedParentFolderId}
            breadcrumbsMap={breadcrumbsMap}
            parentFolders={[...parentFolders, folder.name]}
          />
        )}
      </div>
    </li>
  );
};

export default Folder;
